import './monitoring/init';
import React, { StrictMode, useEffect } from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';

import './assets/fonts/fonts.css';
import './index.css';
import App from './App.tsx';
import { useAuthStore } from './store/auth.store';
import { useViewModeStore } from './store/viewMode.store';
import { useGuestJobStore } from './store/guestJob.store';
import { HYDRATING } from './utils/hydrating';
import { i18nReady } from './i18n/config';
import { captureHydrationMismatch } from './monitoring/sentry';
import { createAppRouter } from './router';
import { routes } from './router/routes';
import { preResolveLazyRoutes } from './prerender/preResolveRoutes';
import { loadInlineState } from './prerender/state';
import { safeSessionStorage } from './utils/safeStorage';

// P1: serialized page state must be available before the first render.
loadInlineState();

const CHUNK_RELOAD_KEY = 'bricolat:last-chunk-reload';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

window.addEventListener('vite:preloadError', (event) => {
  const lastReloadAt = Number(safeSessionStorage.getItem(CHUNK_RELOAD_KEY));
  if (Number.isFinite(lastReloadAt) && Date.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) return;

  // The cooldown needs a marker that survives the reload: without one, a missing chunk would reload forever.
  if (!safeSessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))) return;

  event.preventDefault();
  window.location.reload();
});

// Start MSW in development/E2E mode
async function enableMocking() {
  // Only enable MSW if explicitly requested via VITE_ENABLE_MOCKS
  if (import.meta.env.VITE_ENABLE_MOCKS === 'true') {
    const { startMSW } = await import('./mocks/browser');
    await startMSW();
    console.log('🔶 MSW Browser Worker started (Mock Mode)');
  }
}

function AppWrapper({ router }: { router: ReturnType<typeof createAppRouter> }) {
  const { loadUser } = useAuthStore();

  useEffect(() => {
    // Runs after the children's effects, i.e. after the first (hydration) commit.
    if (HYDRATING) {
      useAuthStore.getState().restoreFromStorage();
      void useViewModeStore.persist.rehydrate();
      void useGuestJobStore.persist.rehydrate();
    }
    if (useAuthStore.getState().isAuthenticated) {
      loadUser();
    }
  }, []);

  return <App router={router} />;
}

const mount = async () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('#root introuvable');

  // Load the page module(s) of this URL before the router exists and before React hydrates: the first render
  // then never suspends, so the server's <Suspense> boundary is adopted instead of replaced by its fallback.
  if (rootElement.children.length > 0) await preResolveLazyRoutes(routes, window.location.pathname);
  const router = createAppRouter();

  if (rootElement.children.length > 0) {
    // Hydrate the HTML prerendered at build time
    hydrateRoot(
      rootElement,
      <StrictMode>
        <AppWrapper router={router} />
      </StrictMode>,
      {
        // Every recoverable error during hydration is a prerender/client mismatch: log it and report it,
        // never hide it (a mismatch makes React replace the server DOM, which is what causes layout shifts).
        onRecoverableError: (error) => {
          const route = window.location.pathname;
          const message = error instanceof Error ? error.message : String(error);
          console.error('[hydration-mismatch]', route, message.slice(0, 500));
          captureHydrationMismatch(error, route);
        },
      },
    );
  } else {
    // Normal SPA render
    createRoot(rootElement).render(
      <StrictMode>
        <AppWrapper router={router} />
      </StrictMode>,
    );
  }
};

// Garantit i18n prêt avant le premier render
i18nReady
  .then(() => enableMocking())
  .then(() => mount());
